import { useEffect } from 'react';

import {
  ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT,
  ZOOM_CHAT_SDK_READY_EVENT,
  ZOOM_CHAT_WINDOW_CLOSED_EVENT
} from './zoomChat.constants';
import { onZoomChatSdkAvailable } from './zoomChatSdk';

type BubbleCall = { hides: boolean };

/**
 * The show and hide calls this hook has made on each SDK that have not failed, oldest first. Module
 * scope, like the other per-SDK state in this feature: the SDK outlives every mount of the hook, so
 * a remount must still know the bubble was hidden by us.
 *
 * The bubble is ours to undo when the latest of these is a hide. That is what limits `show()` to
 * undoing our own hide, which keeps a bubble Zoom or a campaign rule has hidden from being shown
 * here, and what keeps a first load, where nothing has been called, from calling either method at
 * all. A call counts from the moment it is made, and a call that fails is taken out, which leaves
 * the state to the calls before it. So a failure that arrives late, after a newer call, changes
 * nothing it should not: only the call that failed is gone, whatever order the results come in.
 */
const bubbleCalls = new WeakMap<object, BubbleCall[]>();

const isBubbleHiddenByUs = (sdk: object) => {
  const calls = bubbleCalls.get(sdk) ?? [];

  return calls[calls.length - 1]?.hides === true;
};

/**
 * Records a call that has just been made, with what it returned. A call that throws never gets here:
 * it changed nothing. One that returns a promise is held until it settles. A rejection takes it
 * out, and a result that comes back fine settles the state for good, so the calls before it no
 * longer matter and are dropped.
 */
const recordBubbleCall = (sdk: object, hides: boolean, result: unknown) => {
  const call: BubbleCall = { hides };

  bubbleCalls.set(sdk, [...(bubbleCalls.get(sdk) ?? []), call]);

  void Promise.resolve(result).then(
    () => {
      const calls = bubbleCalls.get(sdk) ?? [];
      const index = calls.indexOf(call);

      if (index > 0) {
        bubbleCalls.set(sdk, calls.slice(index));
      }
    },
    () => {
      bubbleCalls.set(
        sdk,
        (bubbleCalls.get(sdk) ?? []).filter((recorded) => recorded !== call)
      );
    }
  );
};

/**
 * Brings the bubble to the wanted state, once. Every call is guarded, in both directions, because
 * Zoom's reference says `hide()` and `show()` fail while a chat window is open, they may do nothing
 * or throw before the runtime is ready, and the callback this runs in is synchronous inside an
 * effect when the SDK already exists: a throw there would reach the app's error boundary and
 * replace the app with the error page. A failure leaves the state as it was, so the next trigger
 * tries again.
 *
 * `hide()` is skipped while a chat is live (`isChatting`, a property, not a function): a chat the
 * patient is in must not be cut off to hide an invitation. It is also skipped when we already hid
 * it, so a repeated trigger does not repeat the call, with one exception: the ready signal repeats
 * it (`repeatHide`). A call made before the runtime is ready may return without an error and still
 * do nothing, so it cannot be trusted as done, and the ready signal is the one moment it can be
 * made again: a patient who clicks into a hidden page before Zoom has finished starting would
 * otherwise keep the bubble for the whole stay. The runtime's `isReady` cannot tell the two cases
 * apart here, because it turns true only after the ready event has been dispatched.
 *
 * A call may return a promise. Until it settles it is taken as done, and a rejection takes it back
 * out (see `bubbleCalls`), so two calls that both fail leave the bubble as it was before either.
 */
const applyBubbleVisibility = (
  sdk: ZoomCampaignSdk,
  isBubbleWanted: boolean,
  { repeatHide = false } = {}
) => {
  try {
    if (isBubbleWanted) {
      if (!isBubbleHiddenByUs(sdk)) {
        return;
      }

      recordBubbleCall(sdk, false, sdk.show?.());

      return;
    }

    if (sdk.isChatting || (isBubbleHiddenByUs(sdk) && !repeatHide)) {
      return;
    }

    recordBubbleCall(sdk, true, sdk.hide?.());
  } catch {
    // Nothing to do here: the bubble stays as it was and the next trigger reconciles it.
  }
};

/**
 * Shows or hides Zoom's bubble to match the page, without touching the chat behind it.
 *
 * The identity hook stays registered across page changes (see ZoomChatSlot), so hiding is only about
 * what the patient sees. `isBubbleWanted` is the slot's answer for the current page and the signed-in
 * patient. It is applied when the SDK appears, when its runtime reports ready, when the patient
 * closes Zoom's window, and when an engagement ends, because each is a moment a call that failed,
 * did nothing or was skipped can be repeated. The last one matters for a chat that was live, and
 * minimized, when the page stopped wanting the bubble (a sign-out that does not reload, say): the
 * hide is skipped while the chat is live, and the end of it may not close any window, so without
 * this the bubble would stay on a page that does not want it. Each trigger acts on the value from
 * the latest render, so a ready signal that arrives late cannot show the bubble on a page the
 * patient has since left or after they have signed out.
 *
 * One set of listeners at a time: the effect removes its own before it runs again, so a StrictMode
 * double effect or a page change leaves exactly one behind. The hook never calls `show()` on a
 * first load, and never calls `hide()` on a document that has no SDK, which is the case on a hidden
 * first page (see the script latch in ZoomChatWidget).
 */
export const useZoomChatBubbleVisibility = (isBubbleWanted: boolean) => {
  useEffect(() => {
    let detach = () => {};

    const stopWaitingForSdk = onZoomChatSdkAvailable((sdk) => {
      const apply = () => applyBubbleVisibility(sdk, isBubbleWanted);
      const applyAtReady = () => applyBubbleVisibility(sdk, isBubbleWanted, { repeatHide: true });
      // Again a turn later, in case the vendor clears `isChatting` after it emits, as
      // useZoomChatLiveEngagement allows for. Both reads go through the same guards: nothing is
      // hidden while a chat is live, and `show()` is only ever the undoing of our own hide.
      let endRetryTimer: number | undefined;
      const applyAtEngagementEnd = () => {
        apply();
        window.clearTimeout(endRetryTimer);
        endRetryTimer = window.setTimeout(apply, 0);
      };

      apply();
      window.addEventListener(ZOOM_CHAT_SDK_READY_EVENT, applyAtReady);

      try {
        sdk.on(ZOOM_CHAT_WINDOW_CLOSED_EVENT, apply);
      } catch {
        // A vendor emitter that rejects a subscription costs the retry on close only; the init and
        // ready triggers still apply, and the call itself is guarded.
      }

      try {
        sdk.on(ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT, applyAtEngagementEnd);
      } catch {
        // Subscribed on its own, so a rejection here costs the retry at the end of a chat only.
      }

      detach = () => {
        window.removeEventListener(ZOOM_CHAT_SDK_READY_EVENT, applyAtReady);
        window.clearTimeout(endRetryTimer);

        try {
          sdk.off(ZOOM_CHAT_WINDOW_CLOSED_EVENT, apply);
        } catch {
          // Unsubscribing is best effort; the handler only reads this closure and guards its calls.
        }

        try {
          sdk.off(ZOOM_CHAT_ENGAGEMENT_ENDED_EVENT, applyAtEngagementEnd);
        } catch {
          // Same, and kept apart so that one failed removal cannot leave the other one standing.
        }
      };
    });

    return () => {
      stopWaitingForSdk();
      detach();
    };
  }, [isBubbleWanted]);
};
